import ErrorBoundary from '@/components/common/ErrorBoundary';
import FadeInBox from '@/components/common/FadeInBox';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { ArrowRight } from '@/components/common/icons';
import { PillSwitcher } from '@/components/common/PillSwitcher';
import { Skeletons } from '@/components/common/Skeletons';
import {
    groupHoverStyleProps,
    hoverStyleProps,
} from '@/components/common/styleHelpers';
import { Paragraph, Text } from '@/components/common/Typography';
import { Divider, Grid, GridProps, Spinner } from '@chakra-ui/react';
import { PlentyGoalAccountFinancialEntity } from '@common/financialEntities';
import { Goal } from '@common/goals';
import {
    getGoalAccountOwnershipDetails,
    useUpsertMyGoalAccountOnboarding,
} from '@common/react/goals';
import { getPartnerName, useCurrentUser } from '@common/react/user';
import { useState } from 'react';
import GoalActivitiesSection from '../GoalActivitiesSection';
import GoalDetailInvestmentInfoSection from '../GoalDetailInvestmentInfoSection';
import GoalDetailPositionsItem from '../GoalDetailPositionsItem';
import GoalLockup from '../GoalLockup';
import InvestmentValuesSection from '../InvestmentValuesSection';
import RiskPortfolioSlider from '../RiskPortfolioSlider';
import { useGoalDetailContext } from './GoalDetailContext';

// N.B. Try to keep this logic in sync with GoalAccountSection in mobile/
export default function GoalDetailAccountsSection() {
    const [viewType, setViewType] = useState<'MINE' | 'THEIRS'>('MINE');
    const { partnerInfo } = useCurrentUser();
    const { goalUnfiltered } = useGoalDetailContext();
    if (!goalUnfiltered) return <></>;
    const details = getGoalAccountOwnershipDetails(goalUnfiltered);
    if (!details) return <></>;

    if (
        details.ownershipType === 'JOINT' ||
        details.ownershipType === 'PRIVATE'
    ) {
        return (
            <>
                <FlexRow px={16} pt={4} pb={8}>
                    <Divider borderColor="gray.300" />
                </FlexRow>
                <GoalAccountSection goalAccount={details.goalAccount} />
            </>
        );
    }

    // If we got here, the goal is Split.
    // Show appropriate section based on whether current user has onboarded.

    const myGoalAccount = details.myGoalAccount;
    const partnerGoalAccount = details.partnerGoalAccount;

    return (
        <>
            <FlexRow pl={12} pr={16} pt={6} pb={8} align="center" gap={4}>
                <PillSwitcher
                    flexShrink={0}
                    options={[
                        { value: 'MINE', label: 'Your investment' },
                        {
                            value: 'THEIRS',
                            label: `${getPartnerName(partnerInfo)}’s investment`,
                            tooltip: !partnerGoalAccount
                                ? `${getPartnerName(partnerInfo)} has not begun investing yet`
                                : undefined,
                            disabled: !partnerGoalAccount,
                        },
                    ]}
                    currentValue={viewType}
                    onChange={(v) => setViewType(v)}
                />
                <Divider />
            </FlexRow>
            <FadeInBox>
                {viewType === 'MINE' ? (
                    myGoalAccount ? (
                        <GoalAccountSection goalAccount={myGoalAccount} />
                    ) : (
                        <PreonboardingGoalAccountSection
                            goalUnfiltered={goalUnfiltered}
                        />
                    )
                ) : partnerGoalAccount ? (
                    <GoalAccountSection goalAccount={partnerGoalAccount} />
                ) : (
                    // TODO: Consider adding "partner onboarding" section
                    <></>
                )}
            </FadeInBox>
        </>
    );
}

const GoalAccountSection = ({
    goalAccount,
}: {
    goalAccount: PlentyGoalAccountFinancialEntity;
}) => {
    const { goal } = useGoalDetailContext();

    return (
        <GoalTwoColumnLayout>
            <FlexCol>
                <ErrorBoundary>
                    <>
                        <GoalDetailInvestmentInfoSection
                            goalAccount={goalAccount}
                        />
                        <Divider my={8} />
                        <RiskPortfolioSlider goalAccount={goalAccount} />
                        {goalAccount.info.investmentType ===
                            'DIRECT_INDEXING' && (
                            <>
                                <GoalDetailPositionsItem
                                    financialEntityId={goalAccount.id}
                                />
                                <Divider my={8} />
                                <InvestmentValuesSection />
                            </>
                        )}
                    </>
                </ErrorBoundary>
            </FlexCol>
            <FlexCol>
                {goal ? (
                    <GoalActivitiesSection
                        goal={goal}
                        filterByGoalAccountId={goalAccount.id}
                    />
                ) : (
                    <Skeletons count={4} w="80%" h={8} mb={8} />
                )}
            </FlexCol>
        </GoalTwoColumnLayout>
    );
};

const PreonboardingGoalAccountSection = ({
    goalUnfiltered,
}: {
    goalUnfiltered: Goal;
}) => {
    const [submitting, setSubmitting] = useState(false);
    const { partnerInfo } = useCurrentUser();
    const { setPosition } = useGoalDetailContext();
    const upsertMyGoalAccountOnboarding =
        useUpsertMyGoalAccountOnboarding(goalUnfiltered);

    const startOnboarding = () => {
        setSubmitting(true);
        upsertMyGoalAccountOnboarding({}).then(() => {
            setSubmitting(false);
            setPosition('ONBOARDING');
        });
    };

    return (
        <GoalTwoColumnLayout>
            <FlexCol>
                <Text fontWeight="medium" fontSize="md" mb={4}>
                    Your goal account
                </Text>
                <FlexRow
                    align="center"
                    justify="space-between"
                    role="group"
                    p={4}
                    mx={-4}
                    rounded="md"
                    border="1px solid"
                    borderColor="white"
                    {...hoverStyleProps({
                        borderColor: 'blue.400',
                    })}
                    onClick={startOnboarding}
                >
                    <GoalLockup
                        iconProps={{
                            mr: 3,
                            filled: true,
                        }}
                        goal={goalUnfiltered}
                    />
                    <FlexRow
                        align="center"
                        justify="center"
                        px={4}
                        py={2}
                        gap={2}
                        w={28}
                        h={8}
                        rounded="full"
                        bg="blue.500"
                        transition="200ms all"
                        {...groupHoverStyleProps({
                            bg: 'blue.400',
                        })}
                    >
                        {submitting ? (
                            <Spinner w={3} h={3} color="white" />
                        ) : (
                            <>
                                <Text fontSize="sm" color="white">
                                    Start now
                                </Text>
                                <ArrowRight color="white" size="sm" />
                            </>
                        )}
                    </FlexRow>
                </FlexRow>
                <Paragraph mt={4} fontSize="sm" color="textMuted">
                    Start investing alongside{' '}
                    {getPartnerName(partnerInfo, 'your partner')} in just a few
                    steps.
                </Paragraph>
            </FlexCol>
        </GoalTwoColumnLayout>
    );
};

const GoalTwoColumnLayout = (props: GridProps) => {
    return (
        <Grid
            px={16}
            templateRows="auto"
            templateColumns={{
                base: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
            }}
            columnGap={16}
            rowGap={8}
            w="full"
            {...props}
        />
    );
};
